import React, { FC, useEffect } from 'react';
import Quill from 'quill';
import {
    BoldOutlined,
    ItalicOutlined,
    UnderlineOutlined,
    StrikethroughOutlined,
    LinkOutlined,
} from '@ant-design/icons';
import 'quill/dist/quill.snow.css';
import styled from 'styled-components';
import { useQuill } from 'react-quilljs';
import { Divider } from 'antd';

import { Code } from '../Icons/Code';
import { useTheme } from '../../../hooks/useTheme';

interface IProps {
    containerHeight?: number;
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    isDisabled?: boolean;
}

// Кастомный блот для жирного текста
class Bold extends Quill.import('formats/bold') {
    static create(value) {
        const node = super.create(value);
        return node;
    }
}

Bold.blotName = 'bold';
Bold.tagName = 'b';
Quill.register(Bold);

// Кастомный блот для курсивного текста
class Italic extends Quill.import('formats/italic') {
    static create(value) {
        const node = super.create(value);
        return node;
    }
}

Italic.blotName = 'italic';
Italic.tagName = 'i';
Quill.register(Italic);

// Кастомный блот для спойлерного текста
class Spoiler extends Quill.import('formats/code') {
    static create(value) {
        const node = super.create(value);
        return node;
    }
}

Spoiler.blotName = 'spoiler';
Spoiler.tagName = 'tg-spoiler';
Quill.register(Spoiler);

export const TelegramTextEditor: FC<IProps> = ({
    containerHeight = 230,
    value,
    onChange,
    placeholder,
    isDisabled,
}) => {
    const icons = Quill.import('ui/icons');
    icons.bold = null;
    icons.italic = null;
    icons.underline = null;
    icons.strike = null;
    icons.link = null;
    const hex = +Date.now();

    const { quillRef, quill } = useQuill({
        modules: {
            toolbar: `#toolbar${hex}`,
        },
        formats: [
            'bold',
            'italic',
            'underline',
            'strike',
            'code',
            'link',
            'blockquote',
            'spoiler',
        ],
        placeholder: placeholder || 'Начните писать',
    });

    useEffect(() => {
        if (quill && value) {
            quill.clipboard.dangerouslyPasteHTML(value);
        }
        if (quill && onChange) {
            quill.on('text-change', () => {
                onChange(quill.root.innerHTML);
            });
        }
        if (quill && isDisabled) {
            quill.enable(false);
        }

        if (quill) {
            const handleKeyDown = event => {
                if (event.key === 'Enter') {
                    event.preventDefault();

                    const range = quill.getSelection(true);
                    const currentText = quill.getText(range.index - 1, 1);

                    if (currentText.trim() === '') {
                        quill.insertText(range.index, '\n');
                    } else {
                        quill.insertText(range.index, '\n');
                    }

                    quill.setSelection(range.index + 1, 0);
                }
            };

            quill.root.addEventListener('keydown', handleKeyDown);

            return () => {
                quill.root.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [quill]);

    return (
        <StyledContainer containerHeight={containerHeight}>
            <div
                id={`toolbar${hex}`}
                className={`ql-toolbar ql-snow ${
                    isDisabled ? 'ql-disabled' : ''
                }`}
            >
                <button className='ql-bold'>
                    <BoldOutlined />
                </button>
                <button className='ql-italic'>
                    <ItalicOutlined />
                </button>
                <button className='ql-underline'>
                    <UnderlineOutlined />
                </button>
                <button className='ql-strike'>
                    <StrikethroughOutlined />
                </button>
                <button className='ql-code'>
                    <Code />
                </button>
                <button className='ql-spoiler'>
                    <StyledSpoilerButton></StyledSpoilerButton>
                </button>
                <button className='ql-blockquote'></button>
                <Divider type='vertical' />
                <button className='ql-link'>
                    <LinkOutlined />
                </button>
            </div>

            <div ref={quillRef} />
        </StyledContainer>
    );
};

const StyledSpoilerButton = styled.div`
    width: 16px;
    height: 16px;
    background: repeating-linear-gradient(
        -45deg,
        transparent,
        transparent 2px,
        rgb(0, 0, 0) 2px,
        rgb(204, 204, 204) 4px
    );
    &:hover {
        background: repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 2px,
            #06c 2px,
            rgb(204, 204, 204) 4px
        );
    }
`;

const StyledContainer = styled.div<{ containerHeight: number }>`
    width: auto;
    display: flex;
    flex-direction: column;
    flex: 1;

    p {
        display: inline !important;
    }

    @media (max-width: 768px) {
        width: auto;
    }

    .ql-toolbar {
        border: unset;
        padding: 0 0 10px 0;
    }

    .ql-container {
        min-height: ${props => props.containerHeight + 'px'};
        border: 1px dashed rgba(37, 33, 41, 0.15) !important;
        border-radius: 5px;
        resize: vertical;
    }

    .ql-editor {
        min-height: ${props => props.containerHeight + 'px'};
    }

    tg-spoiler {
        background-color: #cccccc;
        background-size: 8px 8px;
        background-repeat: repeat;
        background-position: 11px 0px, 20.4px 10.4px;
        background-image: radial-gradient(circle, #ededed 1px, transparent 1px),
            radial-gradient(circle, #ededed 1px, transparent 1px);
    }

    .ql-link {
        float: none !important;
    }

    .ql-active {
        ${StyledSpoilerButton} {
            background: repeating-linear-gradient(
                -45deg,
                transparent,
                transparent 2px,
                #06c 2px,
                rgb(204, 204, 204) 4px
            );
        }
    }
`;
